import { Button, Form, Input, InputNumber, Select, Slider, ColorPicker, Tabs, Switch } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState, useMemo } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import BusinessSerializeSelector from "@/components/BusinessSerializeSelector";
import { getComponentMeta, VISUALIZATION_OPTIONS } from "../registry/componentRegistry";
import { getPreviewComponent, getRealisticPreviewImage } from "../registry/componentPreviews";
import { useGetDashboardListQuery } from "@/redux/api/dashboardApi";
import { useSelector } from "react-redux";

/**
 * ComponentConfigModal – Configuration modal for live dashboard components.
 *
 * Follows the exact same UX structure as DashboardAddItemModal from Tile Dashboard:
 * - Header with back button + "Edit {ComponentLabel}" title
 * - Live SVG / Realistic preview area
 * - Tabbed form (Basic, Data, Visualization, Link, Appearance)
 * - Footer with Create/Update + Cancel buttons
 *
 * Uses COMPONENT_REGISTRY via getComponentMeta() — fixes "Edit undefined".
 * Uses componentPreviews — fixes "Not Supported" preview.
 */
const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ");
};

const getBusinessId = (b) => {
  if (!b) return undefined;
  if (typeof b === "string") return b;
  if (Array.isArray(b)) {
    const ids = b.map((item) => (typeof item === "object" ? item?._id || item?.id : item)).filter(Boolean);
    if (ids.length === 0) return undefined;
    return ids.length === 1 ? ids[0] : ids;
  }
  if (typeof b === "object") return b._id || b.id;
  return undefined;
};

const ComponentConfigModal = ({
  show,
  onClose,
  onSubmit,
  onBack,
  mode = "create",
  componentType,
  initialData,
  dashboardData = {},
}) => {
  const [form] = Form.useForm();
  const [selectedTab, setSelectedTab] = useState("basic");
  const { business } = useSelector((state) => state.user);

  // Get component metadata from registry (guaranteed non-null)
  const componentMeta = getComponentMeta(componentType);

  // Live preview — tracks form values for real-time preview update
  const [previewColor, setPreviewColor] = useState(componentMeta.color);

  // Get the SVG preview component
  const PreviewComponent = getPreviewComponent(componentType);

  // Filter visualization options to what this component supports
  const availableVisualizations = VISUALIZATION_OPTIONS.filter(
    (opt) => componentMeta.visualizations?.includes(opt.value)
  );

  // Watch form fields for conditional rendering
  const selectedBusiness = Form.useWatch("business", form);
  const selectedVisualization = Form.useWatch("visualization", form);
  const selectedLinkTargetType = Form.useWatch("linkTargetType", form);
  const selectedLinkDashboardId = Form.useWatch("linkDashboardId", form);
  const selectedDesignType = Form.useWatch("designType", form);
  const activeDesignType = selectedDesignType || initialData?.styles?.designType || "realistic";
  const [submitting, setSubmitting] = useState(false);

  // Fetch dashboards for Linking tab
  const { data: dashboardsData, isFetching: loadingDashboards } = useGetDashboardListQuery(
    {
      limit: 1000,
      business: selectedBusiness
        ? JSON.stringify(Array.isArray(selectedBusiness) ? selectedBusiness : [selectedBusiness])
        : undefined,
    },
    { skip: !show || !selectedBusiness }
  );
  const dashboardsList = dashboardsData?.results || [];

  const targetDashboards = useMemo(() => {
    if (!selectedLinkTargetType) return [];
    return dashboardsList.filter((d) => {
      const type = d.type || "tile";
      return type === selectedLinkTargetType;
    });
  }, [dashboardsList, selectedLinkTargetType]);

  const targetTabs = useMemo(() => {
    if (!selectedLinkDashboardId) return [];
    const targetDb = dashboardsList.find((d) => d._id === selectedLinkDashboardId);
    if (!targetDb?.hasTabs || !Array.isArray(targetDb?.tabs) || targetDb.tabs.length === 0) return [];
    return targetDb.tabs;
  }, [dashboardsList, selectedLinkDashboardId]);

  const visualizationOptions = availableVisualizations.length > 0 ? availableVisualizations : VISUALIZATION_OPTIONS;

  // Set initial form values
  useEffect(() => {
    if (!show) return;

    const dashBus = getBusinessId(dashboardData?.business) || getBusinessId(business);
    const dashSite = getBusinessId(dashboardData?.site);

    if (initialData) {
      // Editing existing component
      const colorVal = initialData.styles?.color || componentMeta.color;
      const minV = initialData.config?.minValue ?? componentMeta.defaultConfig?.minValue ?? 0;
      const maxV = initialData.config?.maxValue ?? componentMeta.defaultConfig?.maxValue ?? 100;
      const range = maxV - minV;
      const calcGreenEnd = Math.round(minV + range * 0.35);
      const calcYellowEnd = Math.round(minV + range * 0.70);

      const greenStart = initialData.config?.greenStart ?? minV;
      const greenEnd = initialData.config?.greenEnd ?? calcGreenEnd;
      const yellowStart = initialData.config?.yellowStart ?? greenEnd;
      const yellowEnd = initialData.config?.yellowEnd ?? calcYellowEnd;
      const redStart = initialData.config?.redStart ?? yellowEnd;
      const redEnd = initialData.config?.redEnd ?? maxV;

      form.setFieldsValue({
        title: initialData.title || componentMeta.label,
        visualization: initialData.config?.visualization || componentMeta.defaultConfig?.visualization || "value",
        minValue: minV,
        maxValue: maxV,
        unit: initialData.config?.unit || componentMeta.defaultConfig?.unit || "",
        fontSize: Number(initialData.config?.fontSize ?? initialData.styles?.fontSize ?? 14),
        color: colorVal,
        opacity: initialData.styles?.opacity ?? 1,
        designType: initialData.styles?.designType || "realistic",
        showLabel: initialData.config?.showLabel ?? true,
        business: getBusinessId(initialData.config?.business) || dashBus,
        site: getBusinessId(initialData.config?.site) || dashSite,
        device: initialData.deviceId,
        yAxis: initialData.parameterId,
        minLevel: initialData.config?.minLevel ?? null,
        maxLevel: initialData.config?.maxLevel ?? null,
        greenStart,
        greenEnd,
        yellowStart,
        yellowEnd,
        redStart,
        redEnd,
        linkTargetType: initialData.config?.linkTargetType || undefined,
        linkDashboardId: initialData.config?.linkDashboardId || undefined,
        linkTabId: initialData.config?.linkTabId || undefined,
      });
      setPreviewColor(colorVal);
    } else {
      // Creating new component — use registry defaults
      const defMin = componentMeta.defaultConfig?.minValue ?? 0;
      const defMax = componentMeta.defaultConfig?.maxValue ?? 100;
      const range = defMax - defMin;
      const calcGreenEnd = Math.round(defMin + range * 0.35);
      const calcYellowEnd = Math.round(defMin + range * 0.70);

      form.resetFields();
      form.setFieldsValue({
        visualization: componentMeta.defaultConfig?.visualization || "value",
        minValue: defMin,
        maxValue: defMax,
        unit: componentMeta.defaultConfig?.unit || "",
        fontSize: 14,
        color: componentMeta.color,
        opacity: 1,
        designType: "realistic",
        showLabel: true,
        business: dashBus,
        site: dashSite,
        minLevel: null,
        maxLevel: null,
        greenStart: defMin,
        greenEnd: calcGreenEnd,
        yellowStart: calcGreenEnd,
        yellowEnd: calcYellowEnd,
        redStart: calcYellowEnd,
        redEnd: defMax,
        linkTargetType: undefined,
        linkDashboardId: undefined,
        linkTabId: undefined,
      });
      setPreviewColor(componentMeta.color);
    }
    setSelectedTab("basic");
  }, [show, initialData, componentType, dashboardData]);

  // Handle form submission
  const handleFinish = async () => {
    try {
      setSubmitting(true);
      const values = form.getFieldsValue(true);

      const colorStr = typeof values.color === "string"
        ? values.color
        : values.color?.toHexString?.() || componentMeta.color;

      const fontSizeVal = Number(values.fontSize) || 14;

      const nodeConfig = {
        title: values.title || "",
        config: {
          visualization: values.visualization || "",
          minValue: values.minValue ?? 0,
          maxValue: values.maxValue ?? 100,
          unit: values.unit || "",
          fontSize: fontSizeVal,
          showLabel: values.showLabel ?? true,
          business: values.business,
          site: values.site,
          minLevel: values.minValue ?? 0,
          maxLevel: values.maxValue ?? 100,
          greenStart: values.greenStart,
          greenEnd: values.greenEnd,
          yellowStart: values.yellowStart,
          yellowEnd: values.yellowEnd,
          redStart: values.redStart,
          redEnd: values.redEnd,
          linkTargetType: values.linkTargetType || null,
          linkDashboardId: values.linkDashboardId || null,
          linkTabId: values.linkTabId || null,
        },
        styles: {
          color: colorStr,
          opacity: values.opacity ?? 1,
          designType: values.designType || "realistic",
          fontSize: fontSizeVal,
        },
        // Device binding
        deviceId: values.device || null,
        parameterId: values.yAxis || null,
      };

      await onSubmit(nodeConfig);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Submission failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle back button
  const handleBack = () => {
    form.resetFields();
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered dialogClassName="dashboard-modal">
      {/* ─── Header ──────────────────────────────────────────── */}
      <Modal.Header closeButton>
        <div className="d-flex align-items-center justify-content-between w-100">
          <div>
            <Button color="default" variant="text" size="small" onClick={handleBack}>
              <MdOutlineKeyboardBackspace className="fs-4" />
            </Button>
          </div>
          <Modal.Title className="fs-6">
            {mode === "edit" ? "Edit" : "Configure"} {componentMeta.label}
          </Modal.Title>
          <div className="opacity-0">.</div>
        </div>
      </Modal.Header>

      {/* ─── Live SVG / Realistic Preview ───────────────────────── */}
      <Modal.Body className="p-4 bg-body-tertiary">
        <div className="mb-1 text-muted small">Live Preview</div>
        <div
          className="d-flex align-items-center justify-content-center rounded-3 live-config-preview"
          style={{
            height: 120,
            backgroundColor: `${previewColor}08`,
            border: `1px dashed ${previewColor}40`,
          }}
        >
          {activeDesignType === "realistic" && getRealisticPreviewImage(componentType) ? (
            <img
              src={getRealisticPreviewImage(componentType)}
              alt="Realistic SCADA Component"
              style={{
                maxHeight: 100,
                maxWidth: 130,
                objectFit: "contain",
                filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.25))",
              }}
            />
          ) : PreviewComponent ? (
            <PreviewComponent color={previewColor} size={80} />
          ) : (
            <span className="text-muted">Preview not available</span>
          )}
        </div>
      </Modal.Body>

      {/* ─── Tabbed Form ──────────────────────────────────────── */}
      <Modal.Body className="p-4">
        <Form
          layout="vertical"
          form={form}
          onFinish={handleFinish}
          scrollToFirstError
          className="mt-3"
          onValuesChange={(changed) => {
            // Live preview: update color when changed
            if ("color" in changed) {
              const c = typeof changed.color === "string" ? changed.color : changed.color?.toHexString?.();
              if (c) setPreviewColor(c);
            }
            if ("minValue" in changed || "maxValue" in changed) {
              const currentMin = form.getFieldValue("minValue") ?? 0;
              const currentMax = form.getFieldValue("maxValue") ?? 100;
              const range = currentMax - currentMin;
              const calcGreenEnd = Math.round(currentMin + range * 0.35);
              const calcYellowEnd = Math.round(currentMin + range * 0.70);
              form.setFieldsValue({
                greenStart: currentMin,
                greenEnd: calcGreenEnd,
                yellowStart: calcGreenEnd,
                yellowEnd: calcYellowEnd,
                redStart: calcYellowEnd,
                redEnd: currentMax,
              });
            }
            if ("greenEnd" in changed) {
              form.setFieldsValue({ yellowStart: changed.greenEnd });
            }
            if ("yellowEnd" in changed) {
              form.setFieldsValue({ redStart: changed.yellowEnd });
            }
            if ("yellowStart" in changed) {
              form.setFieldsValue({ greenEnd: changed.yellowStart });
            }
            if ("redStart" in changed) {
              form.setFieldsValue({ yellowEnd: changed.redStart });
            }
            if ("business" in changed) {
              form.setFieldsValue({
                linkDashboardId: undefined,
                linkTabId: undefined,
              });
            }
            if ("linkTargetType" in changed) {
              form.setFieldsValue({
                linkDashboardId: undefined,
                linkTabId: undefined,
              });
            }
            if ("linkDashboardId" in changed) {
              form.setFieldsValue({
                linkTabId: undefined,
              });
            }
          }}
        >
          <Tabs
            activeKey={selectedTab}
            onChange={(key) => setSelectedTab(key)}
            items={[
              {
                key: "basic",
                label: "Basic",
                forceRender: true,
                children: (
                  <div className="pt-3">
                    <Form.Item
                      label="Component Title"
                      name="title"
                      rules={[{ required: true, message: "Enter a title for this component" }]}
                    >
                      <Input placeholder={`e.g. Main ${componentMeta.label}`} />
                    </Form.Item>

                    <Form.Item label="Show Label on Canvas" name="showLabel" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: "data",
                label: "Data",
                forceRender: true,
                children: (
                  <div className="pt-3">
                    <p className="text-muted small mb-3">
                      Bind this component to a device parameter for live data updates.
                    </p>
                    <BusinessSerializeSelector
                      business={{
                        isMulti: Array.isArray(dashboardData?.business) && dashboardData.business.length > 1,
                        params: dashboardData?.business ? { _id: getBusinessId(dashboardData?.business) } : {},
                      }}
                      site={{
                        isMulti: false,
                        params: dashboardData?.site ? { _id: getBusinessId(dashboardData?.site) } : {},
                      }}
                      device={{ isMulti: false }}
                      parameter={{ isMulti: false }}
                      form={form}
                    />
                  </div>
                ),
              },
              {
                key: "visualization",
                label: "Visualization",
                forceRender: true,
                children: (
                  <div className="pt-3">
                    <Form.Item label="Visualization Type" name="visualization">
                      <Select
                        options={visualizationOptions}
                        placeholder="Select visualization"
                      />
                    </Form.Item>

                    <Row>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item label="Font Size" name="fontSize">
                          <InputNumber min={8} max={72} placeholder="14" addonAfter="px" className="w-100" />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item label="Min Value" name="minValue">
                          <InputNumber placeholder="0" className="w-100" />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item label="Max Value" name="maxValue">
                          <InputNumber placeholder="100" className="w-100" />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item label="Unit" name="unit">
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select unit"
                            options={[
                              { value: "%", label: "% (Percentage)" },
                              { value: "°C", label: "°C (Celsius)" },
                              { value: "°F", label: "°F (Fahrenheit)" },
                              { value: "bar", label: "bar (Pressure)" },
                              { value: "psi", label: "psi (Pressure)" },
                              { value: "Pa", label: "Pa (Pascal)" },
                              { value: "kPa", label: "kPa (Kilopascal)" },
                              { value: "RPM", label: "RPM (Speed)" },
                              { value: "Hz", label: "Hz (Frequency)" },
                              { value: "V", label: "V (Voltage)" },
                              { value: "A", label: "A (Current)" },
                              { value: "W", label: "W (Watt)" },
                              { value: "kW", label: "kW (Kilowatt)" },
                              { value: "kWh", label: "kWh (Energy)" },
                              { value: "m³/h", label: "m³/h (Flow)" },
                              { value: "L/s", label: "L/s (Flow)" },
                              { value: "L/min", label: "L/min (Flow)" },
                              { value: "m", label: "m (Level/Distance)" },
                              { value: "cm", label: "cm (Level/Distance)" },
                              { value: "kg", label: "kg (Weight)" },
                              { value: "ton", label: "ton (Weight)" },
                              { value: "ppm", label: "ppm (Concentration)" },
                              { value: "pH", label: "pH (Acidity)" },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Dial Threshold Configs */}
                    {selectedVisualization === "gauge" && (
                      <div className="mt-3 border p-3 rounded bg-light">
                        <label className="fw-semibold mb-2">Dial Colors Threshold Ranges</label>
                        <Row className="mb-2">
                          <Col md={6}>
                            <Form.Item label="Green Start" name="greenStart">
                              <InputNumber className="w-100" placeholder="0" />
                            </Form.Item>
                          </Col>
                          <Col md={6}>
                            <Form.Item label="Green End" name="greenEnd">
                              <InputNumber className="w-100" placeholder="35" />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col md={6}>
                            <Form.Item label="Yellow Start" name="yellowStart">
                              <InputNumber className="w-100" placeholder="35" />
                            </Form.Item>
                          </Col>
                          <Col md={6}>
                            <Form.Item label="Yellow End" name="yellowEnd">
                              <InputNumber className="w-100" placeholder="70" />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Item label="Red Start" name="redStart">
                              <InputNumber className="w-100" placeholder="70" />
                            </Form.Item>
                          </Col>
                          <Col md={6}>
                            <Form.Item label="Red End" name="redEnd">
                              <InputNumber className="w-100" placeholder="100" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Threshold Based Animation removed in favor of Min/Max Value */}
                  </div>
                ),
              },
              {
                key: "link",
                label: "Link",
                forceRender: true,
                children: (
                  <div className="pt-3">
                    <p className="text-muted small mb-3">
                      Link this component to trigger navigation when clicked on the live canvas.
                    </p>
                    <Form.Item label="Target Type" name="linkTargetType">
                      <Select
                        placeholder="Select Target Type"
                        options={[
                          { label: "Live Dashboard", value: "live" },
                          { label: "Tile Dashboard", value: "tile" },
                        ]}
                        allowClear
                      />
                    </Form.Item>

                    {selectedLinkTargetType && (
                      <Form.Item label="Target Dashboard" name="linkDashboardId">
                        <Select
                          showSearch
                          placeholder="Select Target Dashboard"
                          loading={loadingDashboards}
                          options={targetDashboards.map((d) => ({
                            label: d.name,
                            value: d._id,
                          }))}
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          allowClear
                        />
                      </Form.Item>
                    )}

                    {selectedLinkDashboardId && targetTabs.length > 0 && (
                      <Form.Item label="Target Tab" name="linkTabId">
                        <Select
                          showSearch
                          placeholder="Select Target Tab"
                          options={targetTabs.map((t) => ({
                            label: stripHtml(t.name) || t.tabId || t._id,
                            value: t.tabId || t._id,
                          }))}
                          allowClear
                        />
                      </Form.Item>
                    )}
                  </div>
                ),
              },
              {
                key: "appearance",
                label: "Appearance",
                forceRender: true,
                children: (
                  <div className="pt-3">
                    <Form.Item label="Component Color" name="color">
                      <ColorPicker format="hex" showText />
                    </Form.Item>

                    <Form.Item label="Opacity" name="opacity">
                      <Slider min={0.1} max={1} step={0.1} marks={{ 0.1: "10%", 0.5: "50%", 1: "100%" }} />
                    </Form.Item>

                    <Form.Item label="Design Type" name="designType">
                      <Select
                        options={[
                          { label: "Realistic SCADA (3D)", value: "realistic" },
                          { label: "Glassmorphism (Modern)", value: "svg" },
                          { label: "Classic Industrial (SVG)", value: "new_design" },
                        ]}
                      />
                    </Form.Item>
                  </div>
                ),
              },
            ]}
          />

          {/* ── Footer ─────────────────────────────────────────── */}
          <div className="d-flex align-items-center justify-content-end gap-2 pt-3">
            <Button type="primary" htmlType="submit" loading={submitting}>
              {mode === "edit" ? "Update" : "Create"}
            </Button>
            <Button type="primary" className="bg-secondary" onClick={onClose} htmlType="button">
              Cancel
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

ComponentConfigModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]),
  componentType: PropTypes.string,
  initialData: PropTypes.object,
};

export default ComponentConfigModal;
